import { GeolocationService } from '../../utils/GeolocationService';
import loadGoogleMapsApi from '../../utils/MapManager';

const SELECTORS = {
	mapContainer: '#map-clinic-profile',
	locationInput: '#origin-location',
	myLocationTrigger: '[data-trigger-location]',
	distanceField: (key) => `[data-distance="${key}"]`,
	distanceTime: '[data-distance-time]',
};

const CLASSESS = {
	distanceUnit: 'clinic__map-unit',
	distanceValue: 'clinic__map-number',
};

export class ClinicProfileMap {
	constructor() {
		this.mapElement = document.querySelector(SELECTORS.mapContainer);
		this.locationInput = document.querySelector(SELECTORS.locationInput);

		const requiredElements = [this.mapElement, this.locationInput];
		if (requiredElements.some((ele) => !ele)) {
			throw new Error('Clinic page map is not initialized');
		}

		this.bindEvents();
		this.setCoordinates();
		this.setup();
	}

	bindEvents() {
		['onLocationChange', 'getUserLocation', 'userLocationSuccess'].forEach((e) => {
			this[e] = this[e].bind(this);
		});
	}

	static init() {
		window.mapQueue.push(() => new ClinicProfileMap());
		loadGoogleMapsApi({ libraries: 'places' });
	}

	/**
	 * Map each Google travel mode to an element key.
	 * @return {{[p: number]: string[]|[string]|[string]}}
	 */
	static get DISTANCE_FIELD_MAP() {
		return {
			[google.maps.TravelMode.DRIVING]: ['driving', 'taxi'],
			[google.maps.TravelMode.WALKING]: ['walking'],
			[google.maps.TravelMode.TRANSIT]: ['transit'],
		};
	}

	setCoordinates() {
		// same as new google.maps.LatLng()
		this.coords = {
			lat: Number(this.mapElement.dataset.lat) || 54.54,
			lng: Number(this.mapElement.dataset.long) || 25.19,
		};
	}

	static fillRow(data, key) {
		const { mode, duration, from, to, status } = data;

		const element = document.querySelector(SELECTORS.distanceField(key));
		const timeElement = element.querySelector(SELECTORS.distanceTime);

		if (status !== 'OK') {
			// Hide row
			element.style.display = 'none';
			return;
		}

		// Link to Google Maps
		element.href = encodeURI(
			`https://www.google.com/maps/dir/?api=1&destination=${to}&origin=${from}&travelmode=${mode.toLowerCase()}`
		);

		// Clear any existing time
		timeElement.innerHTML = '';

		// Split the value into unit and value
		// @example "2 hrs 10 mins"
		duration.split(' ').forEach((e) => {
			const span = document.createElement('span');
			span.classList.add(Number.isNaN(Number(e)) ? CLASSESS.distanceUnit : CLASSESS.distanceValue);
			span.textContent = e;
			timeElement.appendChild(span);
		});

		element.removeAttribute('style');
	}

	/**
	 * Get for each transport mode and fill the element.
	 * @param destination
	 */
	getDistance(destination) {
		// For each Google transport mode
		Object.keys(ClinicProfileMap.DISTANCE_FIELD_MAP).forEach((key) => {
			// Send distance request
			GeolocationService.getDistanceToAddress(
				this.coords,
				destination,
				(data) => {
					// Fill all elements that correspond to that Google transport mode type.
					ClinicProfileMap.DISTANCE_FIELD_MAP[key].forEach((element) =>
						ClinicProfileMap.fillRow(data, element)
					);
				},
				[key]
			);
		});
	}

	/**
	 * If location share is allowed get the distance and fill the input.
	 * @param data
	 */
	userLocationSuccess(data) {
		this.locationInput.value = data.address;
		this.getDistance(data.coords);
	}

	/**
	 * When a user shares their location.
	 */
	getUserLocation() {
		if (!this.locationService) {
			this.locationService = new GeolocationService(this.userLocationSuccess);
		} else {
			this.locationService.getGeolocation();
		}
	}

	/**
	 * When autocomplete location has been change extract the location
	 * and get the distance.
	 */
	onLocationChange() {
		const place = this.autocomplete.getPlace();
		if (!place?.geometry?.location) {
			// User entered the name of a Place that was not suggested and
			// pressed the Enter key, or the Place Details request failed.
			/* eslint no-alert: "off" */
			window.alert(`No details available for input: '${place.name}'`);
			return;
		}
		this.getDistance(place.geometry.location);
	}

	setupEventListeners() {
		this.autocomplete.addListener('place_changed', this.onLocationChange);
		this.locationTrigger?.addEventListener('click', this.getUserLocation);
	}

	/**
	 * Setup map and the pin.
	 */
	setupMap() {
		this.map = new google.maps.Map(this.mapElement, {
			center: this.coords,
			zoom: 15,
			styles: window.MAP_STYLE,
		});
		this.marker = new google.maps.Marker({
			position: this.coords,
			map: this.map,
		});
	}

	setupAutocomplete() {
		this.autocomplete = new google.maps.places.Autocomplete(this.locationInput);
		// Set the data fields to return when the user selects a place.
		this.autocomplete.setFields(['address_components', 'geometry', 'name']);
	}

	setup() {
		this.setupMap();
		this.setupAutocomplete();

		this.locationTrigger = document.querySelector(SELECTORS.myLocationTrigger);
		this.locationService = null;

		this.setupEventListeners();
	}
}
